/**
 * Notes & Remarks Service
 * 
 * Handles all notes and remarks related API calls
 */

import apiClient, { API_ENDPOINTS } from './api';
import { handleApiError } from '../utils/apiErrorHandler';

const noteService = {
  // ========== NOTES ==========

  /**
   * Get notes for a complaint
   * @param {string} complaintId - Complaint ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} List of notes
   */
  async getNotes(complaintId, params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.notes.list(complaintId), { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch notes' });
    }
  },

  /**
   * Create note for complaint
   * @param {string} complaintId - Complaint ID
   * @param {Object} noteData - Note data (can include files array for attachments)
   * @returns {Promise<Object>} Created note
   */
  async createNote(complaintId, noteData) {
    try {
      // Check if noteData contains files for upload
      const hasFiles = noteData.files && Array.isArray(noteData.files) && noteData.files.length > 0;
      
      if (hasFiles) {
        // Create FormData for file upload
        const formData = new FormData();
        
        // Add text fields
        if (noteData.message) formData.append('message', noteData.message);
        if (noteData.recipients) {
          if (Array.isArray(noteData.recipients)) {
            noteData.recipients.forEach((recipient, index) => {
              formData.append(`recipients[${index}]`, recipient);
            });
          } else {
            formData.append('recipients', noteData.recipients);
          }
        }
        
        // Add files
        noteData.files.forEach((file) => {
          formData.append('attachments[]', file);
        });
        
        const response = await apiClient.post(API_ENDPOINTS.notes.create(complaintId), formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        return {
          success: true,
          data: response.data,
          message: 'Note added successfully',
        };
      } else {
        // Regular JSON request without files
        const response = await apiClient.post(API_ENDPOINTS.notes.create(complaintId), noteData);
        return {
          success: true,
          data: response.data,
          message: 'Note added successfully',
        };
      }
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to create note' });
    }
  },

  /**
   * Update note
   * @param {string} complaintId - Complaint ID
   * @param {string} noteId - Note ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated note
   */
  async updateNote(complaintId, noteId, updateData) {
    try {
      const response = await apiClient.put(
        API_ENDPOINTS.notes.update(complaintId, noteId),
        updateData
      );
      return {
        success: true,
        data: response.data,
        message: 'Note updated successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to update note' });
    }
  },

  /**
   * Delete note
   * @param {string} complaintId - Complaint ID
   * @param {string} noteId - Note ID
   * @returns {Promise<Object>}
   */
  async deleteNote(complaintId, noteId) {
    try {
      await apiClient.delete(API_ENDPOINTS.notes.delete(complaintId, noteId));
      return {
        success: true,
        message: 'Note deleted successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to delete note' });
    }
  },

  /**
   * Send internal note (CVO)
   * @param {string} complaintId - Complaint ID (e.g., "TYO966075714")
   * @param {Object} noteData - Note data with message, sendTo array, and optional files
   * @returns {Promise<Object>} Response from API
   */
  async sendInternalNote(complaintId, noteData) {
    try {
      const formData = new FormData();
      
      // Add complaint_id
      formData.append('complaint_id', complaintId);
      
      // Add message
      if (noteData.message) {
        formData.append('message', noteData.message);
      }
      
      // Add send_to[] array
      if (noteData.sendTo && Array.isArray(noteData.sendTo)) {
        noteData.sendTo.forEach((recipientId) => {
          formData.append('send_to[]', recipientId);
        });
      }
      
      // Add attachments[] array
      if (noteData.files && Array.isArray(noteData.files) && noteData.files.length > 0) {
        noteData.files.forEach((file) => {
          formData.append('attachments[]', file);
        });
      }
      
      const response = await apiClient.post(API_ENDPOINTS.notes.sendInternalNote, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return {
        success: true,
        data: response.data,
        message: response.data?.message || response.data?.msg || 'Internal note sent successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to send internal note' });
    }
  },

  /**
   * Send internal note (Authority)
   * @param {string} complaintId - Complaint ID (e.g., "TYO966075714")
   * @param {Object} noteData - Note data with message, sendTo array, and optional files
   * @returns {Promise<Object>} Response from API
   */
  async sendAuthorityInternalNote(complaintId, noteData) {
    try {
      const formData = new FormData();
      
      // Add complaint_id
      formData.append('complaint_id', complaintId);
      
      // Add message
      if (noteData.message) {
        formData.append('message', noteData.message);
      }
      
      // Add send_to[] array
      if (noteData.sendTo && Array.isArray(noteData.sendTo)) {
        noteData.sendTo.forEach((recipientId) => {
          formData.append('send_to[]', recipientId);
        });
      }
      
      // Add attachments[] array
      if (noteData.files && Array.isArray(noteData.files) && noteData.files.length > 0) {
        noteData.files.forEach((file) => {
          formData.append('attachments[]', file);
        });
      }
      
      const response = await apiClient.post(API_ENDPOINTS.notes.sendAuthorityInternalNote, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      return {
        success: true,
        data: response.data,
        message: response.data?.message || response.data?.msg || 'Internal note sent successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to send internal note' });
    }
  },

  // ========== REMARKS ==========

  /**
   * Get remarks for a complaint
   * @param {string} complaintId - Complaint ID
   * @param {Object} params - Query parameters
   * @returns {Promise<Object>} List of remarks
   */
  async getRemarks(complaintId, params = {}) {
    try {
      const response = await apiClient.get(API_ENDPOINTS.remarks.list(complaintId), { params });
      return {
        success: true,
        data: response.data,
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to fetch remarks' });
    }
  },

  /**
   * Create remark for complaint
   * @param {string} complaintId - Complaint ID
   * @param {Object} remarkData - Remark data
   * @returns {Promise<Object>} Created remark
   */
  async createRemark(complaintId, remarkData) {
    try {
      const response = await apiClient.post(
        API_ENDPOINTS.remarks.create(complaintId),
        remarkData
      );
      return {
        success: true,
        data: response.data,
        message: 'Remark added successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to create remark' });
    }
  },

  /**
   * Update remark
   * @param {string} complaintId - Complaint ID
   * @param {string} remarkId - Remark ID
   * @param {Object} updateData - Data to update
   * @returns {Promise<Object>} Updated remark
   */
  async updateRemark(complaintId, remarkId, updateData) {
    try {
      const response = await apiClient.put(
        API_ENDPOINTS.remarks.update(complaintId, remarkId),
        updateData
      );
      return {
        success: true,
        data: response.data,
        message: 'Remark updated successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to update remark' });
    }
  },

  /**
   * Delete remark
   * @param {string} complaintId - Complaint ID
   * @param {string} remarkId - Remark ID
   * @returns {Promise<Object>}
   */
  async deleteRemark(complaintId, remarkId) {
    try {
      await apiClient.delete(API_ENDPOINTS.remarks.delete(complaintId, remarkId));
      return {
        success: true,
        message: 'Remark deleted successfully',
      };
    } catch (error) {
      return handleApiError(error, { defaultMessage: 'Failed to delete remark' });
    }
  },
};

export default noteService;

